import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import api, { setAuthToken } from "utils/api";
import { ADMIN_BRAND_DEFAULT, ADMIN_ORT_DEFAULT, adminBrandParams } from "utils/brand";
import { queueAuthSuccessPopup } from "utils/authSuccessPopup";
import TeamBrandFilter from "./TeamBrandFilter";
import TeamOrtFilter from "./TeamOrtFilter";
import { TeamAdminBrandProvider } from "./TeamAdminBrandContext";
import { setLogout, setUser } from "state";
import TeamShell from "./TeamShell";
import TeamLogin from "./TeamLogin";
import OverviewTab from "./OverviewTab";
import CalendarTab from "./CalendarTab";
import BookingsTab from "./BookingsTab";
import ManualBookingTab from "./ManualBookingTab";
import ManualRevenueTab from "./ManualRevenueTab";
import MitarbeiterUmsatzTab from "./MitarbeiterUmsatzTab";
import ComparisonTab from "./ComparisonTab";
import CustomersTab from "./CustomersTab";
import BalanceTopUpsTab from "./BalanceTopUpsTab";
import AvailabilityTab from "./AvailabilityTab";
import DealMapTab from "./DealMapTab";
import RechnungenTab from "./RechnungenTab";
import { TeamLocaleProvider, useTeamLocale } from "./TeamLocaleContext";
import { buildRangeFromPreset } from "./DateRangeFilter";
import { TeamAdminLiveProvider } from "./TeamAdminLiveContext";
import TeamAdminNotifications from "./TeamAdminNotifications";
import { useTeamAdminLiveOptional } from "./TeamAdminLiveContext";
import "styles/booking.css";
import "styles/team.css";

/** Always visible in the top tab row (desktop). */
const PRIMARY_TAB_IDS = ["manual", "overview", "calendar", "bookings", "rechnungen", "mitarbeiterUmsatz"];
/** Shown in the overflow menu on desktop; on mobile all tabs live in the menu. */
const OVERFLOW_TAB_IDS = ["dealmap", "revenue", "comparison", "availability", "customers", "topups"];
const ALL_TAB_IDS = [...PRIMARY_TAB_IDS, ...OVERFLOW_TAB_IDS];

const useNarrowNav = () => {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 1180px)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1180px)");
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return narrow;
};

const OverviewAutoRefresh = ({ activeTab, loadOverview }) => {
  const live = useTeamAdminLiveOptional();
  useEffect(() => {
    if (!live?.refreshTick) return;
    loadOverview({ silent: true });
  }, [live?.refreshTick, loadOverview, activeTab]);
  return null;
};

const TeamDashboard = ({ user, onLogout }) => {
  const { t } = useTeamLocale();
  const narrowNav = useNarrowNav();
  const [activeTab, setActiveTab] = useState("manual");
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(() => buildRangeFromPreset("all"));
  const [brandFilter, setBrandFilter] = useState(ADMIN_BRAND_DEFAULT);
  const [ortFilter, setOrtFilter] = useState(ADMIN_ORT_DEFAULT);

  const rowTabIds = narrowNav ? [] : PRIMARY_TAB_IDS;
  const menuTabIds = narrowNav ? ALL_TAB_IDS : OVERFLOW_TAB_IDS;
  const overflowActive = OVERFLOW_TAB_IDS.includes(activeTab);

  useEffect(() => {
    document.body.classList.toggle("team-menu-open", navMenuOpen);
    return () => document.body.classList.remove("team-menu-open");
  }, [navMenuOpen]);

  useEffect(() => {
    if (!navMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setNavMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navMenuOpen]);

  const selectTab = useCallback((id) => {
    setActiveTab(id);
    setNavMenuOpen(false);
  }, []);

  const buildActivityParams = useCallback(
    (extra = {}) => adminBrandParams({ brand: brandFilter, ort: ortFilter, ...extra }),
    [brandFilter, ortFilter]
  );

  const loadOverview = useCallback(
    async ({ silent = false } = {}) => {
      if (activeTab !== "overview") return;
      if (!silent) setOverviewLoading(true);
      setError(null);
      try {
        const params = {};
        if (dateRange.from) params.from = dateRange.from;
        if (dateRange.to) params.to = dateRange.to;
        const { data } = await api.get("/admin/overview", {
          params: { ...adminBrandParams({ brand: brandFilter, ort: ortFilter }), ...params },
        });
        setOverview(data);
      } catch (err) {
        if (!silent) {
          setError(err?.response?.data?.message || t("dashboard.loadError"));
        }
      } finally {
        if (!silent) setOverviewLoading(false);
      }
    },
    [activeTab, dateRange.from, dateRange.to, brandFilter, ortFilter, t]
  );

  useEffect(() => {
    loadOverview({ silent: false });
  }, [loadOverview]);

  const onNotificationNavigate = useCallback(
    (tab) => {
      selectTab(tab);
    },
    [selectTab]
  );

  const renderTabButton = (id) => (
    <button
      key={id}
      type="button"
      role="tab"
      aria-selected={activeTab === id}
      className={activeTab === id ? "is-active" : ""}
      onClick={() => selectTab(id)}
    >
      {t(`tabs.${id}`)}
    </button>
  );

  return (
    <TeamAdminLiveProvider
      buildActivityParams={buildActivityParams}
      onNavigate={onNotificationNavigate}
    >
      <OverviewAutoRefresh activeTab={activeTab} loadOverview={loadOverview} />
      <TeamAdminNotifications />
      <section className="team-page-inner">
      <div className="team-nav-shell">
      <nav className="team-tabs" aria-label={t("tabs.navAria")}>
        <Link to="/" className="team-topbar-brand" aria-label={t("common.toWebsiteAria")}>
          <span className="team-topbar-mark" aria-hidden="true">
            <img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt="" />
          </span>
          <span>{t("brand.team")}</span>
        </Link>

        {rowTabIds.length > 0 && (
          <div className="team-tabs-row" role="tablist">
            {rowTabIds.map(renderTabButton)}
          </div>
        )}

        <button
          type="button"
          className={[
            "team-nav-menu-trigger",
            overflowActive && !narrowNav ? "has-active-overflow" : "",
            navMenuOpen ? "is-open" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-label={navMenuOpen ? t("tabs.menuClose") : t("tabs.menuOpen")}
          aria-expanded={navMenuOpen}
          aria-haspopup="true"
          onClick={() => setNavMenuOpen((v) => !v)}
        >
          <span className="team-hamburger" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          {!narrowNav && (
            <span className="team-nav-menu-trigger-label">{t("tabs.more")}</span>
          )}
        </button>

        <button
          type="button"
          className="team-topbar-logout team-topbar-logout--desktop"
          onClick={onLogout}
        >
          {t("common.logout")}
        </button>
      </nav>

      {navMenuOpen && (
        <button
          type="button"
          className="team-nav-menu-backdrop"
          aria-label={t("tabs.menuClose")}
          onClick={() => setNavMenuOpen(false)}
        />
      )}

      <div
        className={`team-nav-drawer ${navMenuOpen ? "open" : ""} ${
          narrowNav ? "is-mobile" : "is-desktop"
        }`}
        aria-hidden={!navMenuOpen}
      >
        <div className="team-nav-drawer-inner" role="tablist" aria-label={t("tabs.navAria")}>
          <p className="team-nav-drawer-title">
            {narrowNav ? t("tabs.navAria") : t("tabs.more")}
          </p>
          {menuTabIds.map(renderTabButton)}
          {narrowNav && (
            <button type="button" className="team-topbar-logout" onClick={onLogout}>
              {t("common.logout")}
            </button>
          )}
        </div>
      </div>
      </div>

      <header className="team-header">
        <div>
          <p className="team-kicker">{t("dashboard.kicker")}</p>
          <h1>
            {t("dashboard.title")} <em>{t("dashboard.titleEm")}</em>
          </h1>
          <p className="team-header-lead">
            {t("dashboard.welcome", {
              name: user?.firstName ? t("dashboard.welcomeName", { firstName: user.firstName }) : "",
            })}
          </p>
        </div>
        <div className="team-header-filters">
          <TeamBrandFilter value={brandFilter} onChange={setBrandFilter} />
          <TeamOrtFilter value={ortFilter} onChange={setOrtFilter} />
        </div>
      </header>

      {error && activeTab === "overview" && (
        <div className="auth-alert is-error">{error}</div>
      )}

      <TeamAdminBrandProvider brand={brandFilter} ort={ortFilter}>
        <div className="team-panel">
          {activeTab === "manual" && <ManualBookingTab />}
          {activeTab === "revenue" && <ManualRevenueTab />}
          {activeTab === "mitarbeiterUmsatz" && <MitarbeiterUmsatzTab />}
          {activeTab === "comparison" && <ComparisonTab />}
          {activeTab === "overview" && (
            <OverviewTab
              data={overview}
              loading={overviewLoading}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              onOpenCustomers={() => selectTab("customers")}
            />
          )}
          {activeTab === "calendar" && <CalendarTab />}
          {activeTab === "bookings" && <BookingsTab />}
          {activeTab === "availability" && <AvailabilityTab />}
          {activeTab === "customers" && <CustomersTab />}
          {activeTab === "topups" && <BalanceTopUpsTab />}
          {activeTab === "dealmap" && <DealMapTab />}
          {activeTab === "rechnungen" && <RechnungenTab />}
        </div>
      </TeamAdminBrandProvider>
      </section>
    </TeamAdminLiveProvider>
  );
};

const TeamAccessDenied = ({ onLogout }) => {
  const { t } = useTeamLocale();
  return (
    <section className="team-login">
      <div className="auth-card team-login-card">
        <p className="auth-kicker">{t("login.kicker")}</p>
        <h1>{t("accessDenied.title")}</h1>
        <p className="auth-lead">{t("accessDenied.lead")}</p>
        <div className="auth-actions">
          <button type="button" className="auth-submit" onClick={onLogout}>
            {t("common.logout")}
          </button>
        </div>
      </div>
    </section>
  );
};

const TeamPageContent = () => {
  const { t } = useTeamLocale();
  const token = useSelector((s) => s.token);
  const user = useSelector((s) => s.user);
  const dispatch = useDispatch();
  const [sessionReady, setSessionReady] = useState(!token);
  const [loginKey, setLoginKey] = useState(0);

  const refreshSession = useCallback(async () => {
    if (!token) {
      setAuthToken(null);
      setSessionReady(true);
      return;
    }
    setAuthToken(token);
    setSessionReady(false);
    try {
      const { data } = await api.get("/auth/me");
      dispatch(setUser({ user: data.user }));
    } catch {
      setAuthToken(null);
      dispatch(setLogout());
    } finally {
      setSessionReady(true);
    }
  }, [token, dispatch]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const handleLogout = () => {
    setAuthToken(null);
    dispatch(setLogout());
    queueAuthSuccessPopup("logout");
    setLoginKey((k) => k + 1);
  };

  return (
    <TeamShell>
      {!sessionReady ? (
        <div className="team-empty">{t("common.loading")}</div>
      ) : !token ? (
        <TeamLogin key={loginKey} onSuccess={refreshSession} />
      ) : !user?.isAdmin ? (
        <TeamAccessDenied onLogout={handleLogout} />
      ) : (
        <TeamDashboard user={user} onLogout={handleLogout} />
      )}
    </TeamShell>
  );
};

const TeamPage = () => (
  <TeamLocaleProvider>
    <TeamPageContent />
  </TeamLocaleProvider>
);

export default TeamPage;
